import { CheckCircle2, File as FileIcon, Loader2, RotateCcw, X, XCircle } from '@/shared/ui/icons';
import { useUploadContext, jobPercent, type UploadJob } from '../../contexts/UploadContext';
import { Progress } from '../../shared/ui/progress';

export const formatBytes = (bytes: number) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(size < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const StatusGlyph = ({ job }: { job: UploadJob }) => {
  if (job.status === 'success') {
    return <CheckCircle2 className="w-4 h-4 text-emerald-500" strokeWidth={2} />;
  }
  if (job.status === 'error') {
    return <XCircle className="w-4 h-4 text-destructive" strokeWidth={2} />;
  }
  // A download is working from the moment it is queued, so it spins while preparing too.
  if (job.status === 'uploading' || job.kind === 'download') {
    return <Loader2 className="w-4 h-4 text-brand animate-spin" strokeWidth={2} />;
  }
  return <FileIcon className="w-4 h-4 text-muted-foreground/60" strokeWidth={2} />;
};

/** The second line of a job row: what it's doing, or what went wrong. A takeoff
 *  import says which project it's landing in, since by the time it finishes the
 *  user is usually somewhere else in the app. */
function JobDetail({ job }: { job: UploadJob }) {
  if (job.status === 'error' && job.error) return <>{job.error}</>;
  if (job.kind === 'download') {
    if (job.status === 'success') return <>{job.fileSize > 0 ? `Ready · ${formatBytes(job.fileSize)}` : 'Ready'}</>;
    if (job.stage === 'Downloading') {
      const parts = ['Downloading'];
      if (job.percent !== undefined) parts.push(`${jobPercent(job)}%`);
      else if (job.fileSize > 0) parts.push(formatBytes(job.fileSize));
      return <>{parts.join(' · ')}</>;
    }
    return <>{job.stage === 'Queued' ? 'Queued' : `${job.stage ?? 'Preparing'}…`}</>;
  }
  if (job.kind === 'takeoff') {
    const sheets = job.sheetCount ?? 0;
    const sheetLabel = `${sheets} sheet${sheets === 1 ? '' : 's'}`;
    if (job.status === 'success') return <>Imported {sheetLabel}</>;
    return <>{job.stage ? `${job.stage} · ` : ''}{sheetLabel}</>;
  }
  if (job.kind === 'site-photo') {
    if (job.status === 'success') return <>Uploaded</>;
    return <>{job.stage ?? 'Waiting to upload'}</>;
  }
  if (job.status === 'success') return <>{formatBytes(job.fileSize)}</>;
  return <>{formatBytes(job.bytesSent)} / {formatBytes(job.fileSize)}</>;
}

export function TransferRow({ job }: { job: UploadJob }) {
  const { retryJob, cancelDownload, cancelUpload } = useUploadContext();
  const percent = jobPercent(job);
  // Documents name the folder; a takeoff import names the project, since it
  // always lands in that project's take-offs folder.
  const destination = job.kind === 'takeoff'
    ? (job.projectName ? `Takeoff · ${job.projectName}` : 'Takeoff')
    : job.kind === 'site-photo'
      ? (job.projectName ? `Site Photos · ${job.projectName}` : 'Site Photos')
      : job.folderName;
  const showBar = job.status === 'uploading' && job.kind !== 'site-photo'
    && (job.kind !== 'download' || job.percent !== undefined);
  const live = job.status === 'pending' || job.status === 'uploading';

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border/30 last:border-b-0">
      <StatusGlyph job={job} />
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] text-foreground truncate">{job.fileName}</div>
        <div className="mt-0.5 flex items-center gap-2">
          <div className="text-[11px] text-muted-foreground truncate">
            <JobDetail job={job} />
          </div>
          {destination && (
            <>
              <span className="text-muted-foreground/40 text-[10px]">·</span>
              <div className="text-[11px] text-muted-foreground truncate">{destination}</div>
            </>
          )}
        </div>
        {showBar && (
          <Progress
            value={percent}
            aria-label={`${job.fileName} ${job.kind === 'download' ? 'download' : 'upload'} progress`}
            className="mt-1 h-[2px] bg-muted/40"
            indicatorClassName="bg-brand"
          />
        )}
      </div>
      {/* Drawing imports and Site Photos retain failed payloads for retry;
          a document upload does once its target folder exists. */}
      {job.status === 'error' && (job.kind === 'takeoff' || job.kind === 'site-photo' || (job.kind === 'file' && job.retryable)) && (
        <button
          type="button"
          aria-label={`Retry ${job.fileName}`}
          onClick={() => retryJob(job.id)}
          className="shrink-0 flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-foreground hover:bg-muted transition-colors"
        >
          <RotateCcw className="w-3 h-3" strokeWidth={2} />
          Retry
        </button>
      )}
      {(job.kind === 'download' || job.kind === 'file') && live && (
        <button
          type="button"
          onClick={() => (job.kind === 'file' ? cancelUpload(job.id) : cancelDownload(job.id))}
          className="shrink-0 p-1 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          aria-label={`Cancel ${job.fileName}`}
        >
          <X className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      )}
    </div>
  );
}
